import { useEffect, useState } from "react";
import { api, currency } from "../lib/api";
import NestPie, { NEST_COLORS, CHART_GRID, CHART_AXIS, CHART_TOOLTIP } from "../components/NestChart";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Plus, Trash2, Pencil, Camera } from "lucide-react";
import { toast } from "sonner";
import useWealth from "../lib/useWealth";
import { accountValue } from "../lib/prices";
import Accounts from "../components/Accounts";
import PriceStatus from "../components/PriceStatus";

const DEFAULT_ASSETS = [
  "Emergency Savings",
  "House deposit LISA",
  "Stocks and Shares ISA",
  "Gold/Silver",
  "Cash",
  "Crypto",
];

const blankAsset = { name: "", amount: "" };

export default function Wealth() {
  const [snaps, setSnaps] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    month: new Date().toISOString().slice(0, 7),
    assets: DEFAULT_ASSETS.map((n) => ({ name: n, amount: "" })),
  });
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/wealth").then((r) => setSnaps(r.data));

  useEffect(() => {
    load();
  }, []);

  const live = useWealth();
  const latest = snaps[snaps.length - 1];
  const total = live.total;
  const accountValues = live.accounts.map((a) => ({
    name: a.name,
    amount: Math.round(accountValue(a, live.quotes).value * 100) / 100,
  }));
  const pieData = accountValues
    .filter((a) => a.amount > 0)
    .map((a) => ({ name: a.name, value: a.amount }));

  // Record today's live values as this month's snapshot.
  const saveLiveSnapshot = async () => {
    const month = new Date().toISOString().slice(0, 7);
    const existing = snaps.find((s) => s.month === month);
    if (existing && !window.confirm(`Replace the ${month} snapshot with today's values?`)) return;
    const payload = { month, assets: accountValues };
    if (existing) await api.put(`/wealth/${existing.id}`, payload);
    else await api.post("/wealth", payload);
    toast.success(`Saved ${month} snapshot`);
    load();
  };

  // Build growth over time (per asset stacked area)
  const allAssetNames = Array.from(
    new Set(snaps.flatMap((s) => s.assets.map((a) => a.name)))
  );
  const growthData = snaps.map((s) => {
    const row = { month: s.month };
    allAssetNames.forEach((n) => {
      const a = s.assets.find((x) => x.name === n);
      row[n] = a ? a.amount : 0;
    });
    return row;
  });

  const startNew = () => {
    setEditingId(null);
    setDraft({
      month: new Date().toISOString().slice(0, 7),
      assets: latest
        ? latest.assets.map((a) => ({ name: a.name, amount: a.amount }))
        : DEFAULT_ASSETS.map((n) => ({ name: n, amount: "" })),
    });
    setShowForm(true);
  };

  const startEdit = (snap) => {
    setEditingId(snap.id);
    setDraft({
      month: snap.month,
      assets: snap.assets.map((a) => ({ name: a.name, amount: a.amount })),
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      month: draft.month,
      assets: draft.assets
        .filter((a) => a.name.trim())
        .map((a) => ({ name: a.name.trim(), amount: parseFloat(a.amount) || 0 })),
    };
    try {
      if (editingId) {
        await api.put(`/wealth/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/wealth", payload);
        toast.success("Added");
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/wealth/${id}`);
    toast.success("Deleted");
    load();
  };

  const setAsset = (idx, patch) => {
    const next = [...draft.assets];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, assets: next });
  };

  return (
    <div className="space-y-8" data-testid="wealth-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-nest-muted">
          Building
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-medium mt-2">
          Wealth Building
        </h1>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <div className="nest-card p-5 md:p-6 col-span-2">
          <div className="text-xs uppercase tracking-widest text-nest-muted">
            Worth now
          </div>
          <div
            className="font-heading text-4xl tabular text-nest-good mt-2"
            data-testid="wealth-total"
          >
            {currency(total)}
          </div>
          <PriceStatus wealth={live} className="mt-2" />
          <button
            onClick={saveLiveSnapshot}
            disabled={!live.accounts.length}
            className="mt-4 text-sm text-nest-brand flex items-center gap-1.5 hover:underline disabled:opacity-40"
            data-testid="save-live-snapshot-btn"
          >
            <Camera size={14} strokeWidth={1.5} /> Save as this month's snapshot
          </button>
        </div>
        <div className="nest-card p-5 md:p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted">
            Accounts
          </div>
          <div className="font-heading text-3xl tabular mt-2">
            {live.accounts.length}
          </div>
        </div>
        <div className="nest-card p-5 md:p-6">
          <div className="text-xs uppercase tracking-widest text-nest-muted">
            Snapshots
          </div>
          <div className="font-heading text-3xl tabular mt-2">{snaps.length}</div>
          {latest && (
            <div className="text-xs text-nest-muted mt-1">latest {latest.month}</div>
          )}
        </div>
      </div>

      <Accounts wealth={live} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="nest-card p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Current allocation
          </div>
          <NestPie data={pieData} testId="wealth-pie" height={280} />
        </div>
        <div className="nest-card p-6 lg:col-span-3">
          <div className="text-xs uppercase tracking-widest text-nest-muted mb-4">
            Growth over time
          </div>
          <div style={{ width: "100%", height: 280 }} data-testid="wealth-growth-chart">
            <ResponsiveContainer>
              <AreaChart data={growthData}>
                <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke={CHART_AXIS} fontSize={11} />
                <YAxis stroke={CHART_AXIS} fontSize={11} tickFormatter={(v) => `£${v}`} />
                <Tooltip
                  formatter={(v) => currency(v)}
                  contentStyle={CHART_TOOLTIP}
                />
                {allAssetNames.map((n, i) => (
                  <Area
                    key={n}
                    type="monotone"
                    dataKey={n}
                    stackId="1"
                    stroke={NEST_COLORS[i % NEST_COLORS.length]}
                    fill={NEST_COLORS[i % NEST_COLORS.length]}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="nest-card p-6" data-testid="wealth-snapshots-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-nest-muted">
              Snapshots
            </div>
            <h3 className="font-heading text-xl mt-1">Monthly history</h3>
          </div>
          <button
            onClick={startNew}
            className="nest-btn-primary flex items-center gap-1 text-sm"
            data-testid="add-wealth-snapshot-btn"
          >
            <Plus size={14} /> New snapshot
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            className="border border-nest-line rounded-2xl p-5 mb-6 bg-nest-surface-1 space-y-4"
            data-testid="wealth-form"
          >
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-widest text-nest-muted">
                Month
              </label>
              <input
                type="month"
                className="nest-input"
                value={draft.month}
                onChange={(e) => setDraft({ ...draft, month: e.target.value })}
                data-testid="wealth-month-input"
              />
            </div>
            <div className="space-y-2">
              {draft.assets.map((a, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input
                    placeholder="Asset name"
                    className="nest-input col-span-6 text-sm"
                    value={a.name}
                    onChange={(e) => setAsset(idx, { name: e.target.value })}
                  />
                  <input
                    placeholder="Amount"
                    type="number"
                    step="0.01"
                    className="nest-input col-span-4 text-sm tabular"
                    value={a.amount}
                    onChange={(e) => setAsset(idx, { amount: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        assets: draft.assets.filter((_, i) => i !== idx),
                      })
                    }
                    className="col-span-2 text-nest-faint hover:text-nest-warn text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setDraft({ ...draft, assets: [...draft.assets, { ...blankAsset }] })
                }
                className="text-sm text-nest-brand hover:underline"
              >
                + Add asset
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="nest-btn-primary text-sm"
                data-testid="wealth-submit-btn"
              >
                {editingId ? "Update" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="text-sm text-nest-muted hover:text-nest-ink"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3" data-testid="wealth-list">
          {snaps.length === 0 && (
            <div className="text-sm text-nest-faint py-6 text-center">
              No snapshots yet
            </div>
          )}
          {[...snaps].reverse().map((s) => {
            const t = s.assets.reduce((sum, a) => sum + a.amount, 0);
            return (
              <div
                key={s.id}
                className="border border-nest-line rounded-2xl p-4"
                data-testid={`wealth-snapshot-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading text-lg">{s.month}</div>
                    <div className="text-xs text-nest-muted mt-1">
                      {s.assets.length} asset{s.assets.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-heading text-xl tabular text-nest-good">
                      {currency(t)}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(s)}
                        className="text-nest-faint hover:text-nest-brand"
                      >
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => remove(s.id)}
                        className="text-nest-faint hover:text-nest-warn"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.assets.map((a, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-nest-surface-2 text-nest-soft px-3 py-1 rounded-full tabular"
                    >
                      {a.name} · {currency(a.amount)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
